import { ChartPie } from "lucide-react";
import OverviewCardHeader from "../Overview/OverviewCardHeader";
import OverviewEmptyState from "../Overview/OverviewEmptyState";
import { PieChart, ResponsiveContainer, Pie, Tooltip, Cell } from "recharts";
import { Card, CardContent } from "../../ui/card";
import { AlertLevelData } from "../interfaces/CommonInterfaces";
import { Skeleton } from "../interfaces/CommonInterfaces";

export default function AlertLevel({
  leveldata,
  loading,
  fromdetails = false,
}: {
  leveldata: AlertLevelData[];
  loading: boolean;
  fromdetails?: boolean;
}) {
  const totalAlerts = leveldata.reduce((sum, item) => sum + item.count, 0);

  return (
    <Card className="overview-card analytics-severity-card h-full">
      <OverviewCardHeader
        fromdetails={fromdetails}
        title="Alerts level"
        description="Alert distribution by severity"
        icon={ChartPie}
      />
      <CardContent className="overview-card-content space-y-4">
        {loading ? (
          <>
            {/* Skeleton for donut chart */}
            <div className="flex items-center justify-center h-48">
              <Skeleton className="w-48 h-48 rounded-full" />
            </div>
            {/* Skeleton for legend */}
            <div className="analytics-severity-legend space-y-2">
              <div className="grid grid-cols-2 gap-1 text-xs font-medium text-muted-foreground">
                <Skeleton className="w-20 h-4" />
                <Skeleton className="w-12 h-4 ml-auto" />
              </div>
              {[...Array(4)].map((_, idx) => (
                <div
                  key={idx}
                  className="analytics-severity-row grid grid-cols-2 gap-1 text-sm"
                >
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-3 h-3 rounded-full" />
                    <Skeleton className="w-24 h-4" />
                  </div>
                  <Skeleton className="w-12 h-4 ml-auto" />
                </div>
              ))}
            </div>
          </>
        ) : leveldata && leveldata.length > 0 ? (
          <>
            {/* Donut Chart */}
            <div className="overview-donut h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={leveldata as any[]}
                    cx="50%"
                    cy="50%"
                    innerRadius={fromdetails ? 68 : 50}
                    outerRadius={fromdetails ? 92 : 90}
                    cornerRadius={fromdetails ? 4 : 0}
                    paddingAngle={2}
                    dataKey="count"
                    isAnimationActive={false}
                  >
                    {leveldata.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    wrapperClassName={
                      fromdetails ? "overview-chart-tooltip" : undefined
                    }
                    formatter={(value) => [`${value}`, ""]}
                  />
                </PieChart>
              </ResponsiveContainer>
              {fromdetails && (
                <div className="overview-donut-label">
                  <strong>{totalAlerts.toLocaleString()}</strong>
                  <span>Total alerts</span>
                </div>
              )}
            </div>

            {/* Legend */}
            <div className="analytics-severity-legend space-y-2">
              <div className="grid grid-cols-2 gap-1 text-xs font-medium text-muted-foreground">
                <div>Level</div>
                <div className="text-right">Count</div>
              </div>
              {leveldata.map((item, index) => (
                <div
                  key={index}
                  className="analytics-severity-row grid grid-cols-2 gap-1 text-sm"
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: item.color }}
                    ></div>
                    <span className="text-foreground">{item.level}</span>
                  </div>
                  <div className="font-medium text-right">{item.count}</div>
                </div>
              ))}
            </div>
          </>
        ) : fromdetails ? (
          <OverviewEmptyState
            icon={ChartPie}
            message="No alerts found in this time range."
          />
        ) : (
          <div className="py-10 text-sm text-center text-muted-foreground">
            No data found.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
